import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { useForm } from './FormManager';
import './Header.css';

const Header = () => {
  const { t, i18n } = useTranslation();
  const { openGeneralForm } = useForm();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [bannerHidden, setBannerHidden] = useState(false);
  const [currentLang, setCurrentLang] = useState(i18n.language);


  // Handle language change
  const changeLanguage = (language) => {
    i18n.changeLanguage(language);
    setCurrentLang(language);
  };

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      // Check if scrolled enough to change header style
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
      
      // Check if banner should be hidden (only on mobile)
      if (window.innerWidth <= 768) {
        if (window.scrollY > 50) {
          setBannerHidden(true);
        } else {
          setBannerHidden(false);
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    
    // Initial check for mobile
    const checkMobile = () => {
      if (window.innerWidth <= 768) {
        setBannerHidden(window.scrollY > 50);
      } else {
        setBannerHidden(false);
      }
    };
    
    window.addEventListener('resize', checkMobile);
    checkMobile();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Get current location for active link detection
  const location = useLocation();
  
  // Navigation links
  const navLinks = [
    { key: 'menuHome', href: '/' },
    { key: 'menuServices', href: '/services', label: 'Leistungen – Websites, SEO, Ads, Buchungssystem, Reputation, E-Mail' },
    // Uncomment and implement subpages for deep linking in the future:
    // { key: 'menuResults', href: '/#results' },
    { key: 'menuPricing', href: '/pricing' },
    { key: 'menuBlog', href: '/blog' },
    { key: 'menuContact', href: '/contact' },
    { key: 'menuAbout', href: '/about' }
  ];

  // Close menu when escape key is pressed
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  return (
    <header className={`header ${scrolled ? 'scrolled' : ''} ${bannerHidden ? 'banner-hidden' : ''}`}>
      <div className="header-inner">
        <Link to="/" className="logo" aria-label="Enzlo home">
          <div className="logo-container">
            <span className="logo-text">Enzlo</span>
            <span className="logo-dot"></span>
          </div>
        </Link>
        
        <div className="header-right">
          <button className="cta-button" type="button" onClick={openGeneralForm}>
            <span className="cta-text">Mehr Kunden gewinnen</span>
            <span className="cta-arrow">→</span>
          </button>
          
          <div className="lang-switcher">
            <button 
              className={`lang-option${currentLang === 'en' ? ' active' : ''}`} 
              onClick={() => changeLanguage('en')} 
              aria-label="Switch to English"
            >
              EN
            </button>
            <span className="lang-divider"></span>
            <button 
              className={`lang-option${currentLang === 'de' ? ' active' : ''}`} 
              onClick={() => changeLanguage('de')} 
              aria-label="Switch to German"
            >
              DE
            </button>
          </div>
          
          <button 
            className={`menu-toggle ${menuOpen ? 'active' : ''}`} 
            onClick={() => setMenuOpen(!menuOpen)} 
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
          >
            <div className="menu-toggle-inner">
              <span className="menu-bar"></span>
              <span className="menu-bar"></span>
            </div>
          </button>
        </div>
      </div>
      
      {menuOpen && (
        <div className="menu-overlay" role="dialog" aria-modal="true">
          <div className="menu-content">
            <div className="menu-header">
              <div className="logo-container menu-logo">
                <span className="logo-text">Enzlo</span>
                <span className="logo-dot"></span>
              </div>
              <button 
                className="close-button" 
                onClick={() => setMenuOpen(false)} 
                aria-label="Close menu"
              >
                <span className="close-icon"></span>
              </button>
            </div>
            
            <nav className="menu-nav">
              {navLinks.map((link, index) => (
                <Link
                  key={link.key}
                  to={link.href}
                  className={`menu-link ${location.pathname === link.href ? 'active' : ''}`}
                  onClick={() => setMenuOpen(false)}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <span className="menu-link-number">0{index + 1}</span>
                  <span className="menu-link-text">{t(link.key)}</span>
                </Link>
              ))}
            </nav>
            
            <div className="menu-footer">
              <button className="cta-button menu-cta" onClick={() => { setMenuOpen(false); openGeneralForm(); }}>
                <span className="cta-text">Mehr Kunden gewinnen</span>
                <span className="cta-arrow">→</span>
              </button>
              
              <div className="menu-lang-switcher">
                <button 
                  className={`menu-lang-option${currentLang === 'en' ? ' active' : ''}`} 
                  onClick={() => changeLanguage('en')} 
                  aria-label="Switch to English"
                >
                  English
                </button>
                <button 
                  className={`menu-lang-option${currentLang === 'de' ? ' active' : ''}`} 
                  onClick={() => changeLanguage('de')} 
                  aria-label="Switch to German"
                >
                  Deutsch
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
